import PropTypes from "prop-types";
import React, { forwardRef, useContext } from "react";

import styled from "styled-components/macro";

import { useNetworkMetadata } from "Flocknote/apiHooks";

import { useCellMeasurements } from "Boards";
import { BoardContext } from "Boards/BoardContext";

import { colors } from "UI/colors";

import { useBoards } from "./BoardContext";
import { Cell, TitleCellWrapper, TitleRowCell } from "./Cells";
import ControlRow from "./ControlRow";
import BoardGroups from "./ControlRow/BoardGroups";
import useBoardLocation from "./ControlRow/hooks/useBoardLocation";
import { TableBody } from "./TableBody";
import { BOARD_TYPES } from "./const";

const HeaderRow = forwardRef(({ shouldHideTotalColumn }, forwardedRef) => {
  const [{ groupID, columns, boardType }] = useBoards();
  const { isReportBoard } = useBoardLocation();

  const { width: totalColWidth } = useCellMeasurements({
    columnID: "totals",
  });

  const canAddColumns =
    boardType === BOARD_TYPES.GROUP ||
    boardType === BOARD_TYPES.REPORT ||
    boardType === BOARD_TYPES.SIGNUP;

  const [state] = useContext(BoardContext);
  const groups = state?.groups;
  const everyoneGroupID = state?.everyoneGroupID;
  const createdBy = state?.createdBy;
  const isShared = state?.isShared;

  const groupsArray =
    groups &&
    Object.entries(groups).map(([ID, group]) => ({
      ID,
      ...group,
      name:
        everyoneGroupID === Number(ID)
          ? `${group.name} (Everyone)`
          : group.name,
    }));

  const hasMultipleGroups = groupsArray?.length > 1;

  return (
    <Wrapper>
      <ControlRow />
      <HeaderRowColumns>
        <FirstCell style={{ width: 170 - 26 }}>
          {isReportBoard && (
            <>
              <span>
                Group
                {hasMultipleGroups ? "s" : ""}
              </span>
              <BoardGroups
                groups={groups}
                hasMultipleGroups={hasMultipleGroups}
                groupsArray={groupsArray}
                createdBy={createdBy}
                isShared={isShared}
              />
            </>
          )}
        </FirstCell>

        <ScrollableInside ref={forwardedRef}>
          {
            // Loop over the headers in each row
            columns.map((column, index) => {
              if (index === 0) return null;
              return (
                <TitleRowCell
                  key={column.ID}
                  columnID={column.ID}
                  columnTitle={column.Header}
                  columnType={column.typeDictID}
                  index={index}
                  totalNumberOfColumns={columns.length}
                  orderBy={column.orderBy}
                  groupID={groupID}
                  isShared={column.isShared}
                  canEdit={column.canEdit}
                  canEditHeader={column.canEditHeader}
                  canFilter={column.canFilter}
                  canDelete={column.canDelete}
                  canAddColumns={canAddColumns}
                  relatedSignups={column?.relatedSignups}
                />
              );
            })
          }
          {!shouldHideTotalColumn && (
            <>
              <SpacerCell />
              <TotalCell style={{ width: totalColWidth || "155px" }}>
                total
              </TotalCell>
            </>
          )}
        </ScrollableInside>
      </HeaderRowColumns>
    </Wrapper>
  );
});

HeaderRow.displayName = "HeaderRow";
export default HeaderRow;

HeaderRow.propTypes = {
  shouldHideTotalColumn: PropTypes.bool,
};

const Wrapper = styled.div`
  flex-grow: 0;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  z-index: 100;
  background: #eceff0;
  margin-right: 8px;
  margin-left: 8px;
  border-radius: 8px;
  box-shadow: rgba(0, 0, 0, 0.2) 1px 1px 3px;

  transition: margin-right 0.4s;

  ${TableBody}.filters_open & {
    margin-right: 329px;
  }
`;
const HeaderRowColumns = styled.div`
  display: flex;
`;
const SpacerCell = styled(Cell)`
  height: 1px;
  border-bottom: none;
  flex-grow: 1;
  flex-shrink: 1;
  width: 1px;
  min-width: 0;
  max-width: none;
`;

const ScrollableInside = styled.div`
  padding-left: 30px;
  display: flex;
  overflow: auto;
  flex-grow: 1;
  position: relative;
  z-index: 1;
  scrollbar-width: none;
  -ms-overflow-style: -ms-autohiding-scrollbar;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const FirstCell = styled(TitleCellWrapper)`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  min-width: 124px;
  flex-grow: 0;
  flex-shrink: 0;
  font-weight: 300;
  padding: 0 5px;
  text-transform: uppercase;
  font-weight: 400;
  font-size: 13px;
  position: relative;
  box-sizing: border-box;
  z-index: 2;
  display: flex;
  align-items: center;

  & > div {
    flex-grow: 0;
  }

  &::after {
    content: "";
    width: 20px;
    top: 0px;
    bottom: 0px;
    position: absolute;
    left: 100%;
    background-image: linear-gradient(
      to right,
      rgb(236, 239, 240),
      rgba(236, 239, 240, 0)
    );
  }

  @media (max-width: 600px) {
    width: 114px;
  }
`;

const TotalCell = styled(TitleCellWrapper)`
  text-transform: uppercase;
  text-align: center;
  font-size: 13px;
  font-weight: 400;
  color: ${colors.fnDkBlue};
  padding-left: 10px;
`;
